import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import BlockIcon from "@mui/icons-material/Block";
import { toast } from "react-toastify";
import Listtable from "components/common/ListTable";
import { patientHistoryMasterApi } from "api/patientHistoryMaster";
import PatientHistoryModal from "./PatientHistoryModal";
import {
  ALL_SPECIALITIES_LABEL,
  SPECIALITY_FILTER_ALL,
  SPECIALITY_FILTER_ANY,
  STATUS_FILTER_OPTIONS,
  buildListParams,
  importFailures,
  importSummary,
} from "./patientHistoryMasterForm";

export const TABLE_HEADERS = ["Name", "Speciality", "Status", "Source", "Action"];
const TABLE_KEYS = ["name", "specialityLabel", "statusLabel", "sourceLabel", "action"];

/** A categorised master shows its Category after the Name. */
const tableColumns = (master) =>
  master.categories?.length
    ? {
        headers: ["Name", "Category", ...TABLE_HEADERS.slice(1)],
        keys: ["name", "category", ...TABLE_KEYS.slice(1)],
      }
    : { headers: TABLE_HEADERS, keys: TABLE_KEYS };

const btnStyle = {
  width: "max-content",
  padding: "0.5rem 2rem",
  color: "#194AF5",
  bgcolor: "#E6EBFF",
  boxShadow: "none",
  borderRadius: "8px",
  ":hover": { bgcolor: "#E6EBFF", color: "#194AF5", boxShadow: "none" },
};

const saveBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const PatientHistoryTab = ({ master, specialityOptions = [] }) => {
  const api = useMemo(() => patientHistoryMasterApi(master.key), [master.key]);

  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [pageNumber, setPageNumber] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [activeRecord, setActiveRecord] = useState({});
  const [deactivateTarget, setDeactivateTarget] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);

  const [searchText, setSearchText] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [specialityFilter, setSpecialityFilter] = useState(SPECIALITY_FILTER_ANY);
  const [statusFilter, setStatusFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const columns = useMemo(() => tableColumns(master), [master]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchText.trim());
      setPageNumber(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchText]);

  const filters = useMemo(
    () => ({
      search: debouncedSearch,
      speciality: specialityFilter,
      status: statusFilter,
      category: categoryFilter,
    }),
    [debouncedSearch, specialityFilter, statusFilter, categoryFilter]
  );

  const fetchRecords = useCallback(() => {
    setLoading(true);

    api
      .list(buildListParams({ page: pageNumber + 1, perPage: rowsPerPage, ...filters }))
      .then((response) => {
        const payload = response?.data || {};
        const list = Array.isArray(payload.data) ? payload.data : [];

        setRows(
          list.map((item) => ({
            ...item,
            action: (
              <Box sx={{ display: "flex", gap: "0.25rem" }}>
                <IconButton
                  aria-label={`Edit ${item.name}`}
                  onClick={() => {
                    setActiveRecord(item);
                    setModalOpen(true);
                  }}
                >
                  <EditIcon fontSize="small" color="primary" />
                </IconButton>
                {item.isActive !== false && (
                  <IconButton
                    aria-label={`Deactivate ${item.name}`}
                    onClick={() => setDeactivateTarget(item)}
                  >
                    <BlockIcon fontSize="small" color="error" />
                  </IconButton>
                )}
              </Box>
            ),
          }))
        );

        const total = Number(payload?.meta?.total);
        setCount(Number.isFinite(total) ? total : list.length);
      })
      .catch((err) => {
        setRows([]);
        setCount(0);
        toast.error(err?.response?.data?.message || "Could not load records.");
      })
      .finally(() => setLoading(false));
  }, [api, pageNumber, rowsPerPage, filters]);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  const handleModalClose = (reason = "save") => {
    setModalOpen(false);
    setActiveRecord({});
    if (reason !== "close") fetchRecords();
  };

  const handleDeactivate = async () => {
    if (!deactivateTarget?._id) return;

    setBusy(true);
    try {
      await api.remove(deactivateTarget._id);
      toast.success(`${master.label} record deactivated successfully`);
      fetchRecords();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not deactivate this record.");
    } finally {
      setBusy(false);
      setDeactivateTarget(null);
    }
  };

  const handleDownload = async (kind, filename) => {
    setBusy(true);
    try {
      const response = await api.download(
        kind,
        kind === "export" ? buildListParams(filters) : {}
      );
      saveBlob(response.data, filename);
    } catch (err) {
      toast.error("Download failed. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const handleImport = async (event) => {
    const file = event?.target?.files?.[0];
    event.target.value = null;
    if (!file) return;

    setBusy(true);
    try {
      const response = await api.importFile(file);
      const summary = importSummary(response?.data);
      const failures = importFailures(response?.data);

      // Failed rows need their reasons, which a toast cannot hold.
      if (failures.length > 0) {
        setImportResult({ message: summary.message, failures });
      } else if (summary.level === "warning") {
        toast.warning(summary.message);
      } else {
        toast.success(summary.message);
      }

      setPageNumber(0);
      fetchRecords();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Import failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "0.5rem",
          padding: "1rem 0",
          flexWrap: "wrap",
        }}
      >
        <TextField
          size="small"
          label="Search"
          value={searchText}
          onChange={(event) => setSearchText(event.target.value)}
          inputProps={{ "data-testid": `history-${master.key}-search` }}
          sx={{ minWidth: 220 }}
        />

        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel id={`history-${master.key}-speciality-label`} shrink>
            Speciality
          </InputLabel>
          <Select
            labelId={`history-${master.key}-speciality-label`}
            label="Speciality"
            notched
            displayEmpty
            value={specialityFilter}
            onChange={(event) => {
              setSpecialityFilter(event.target.value);
              setPageNumber(0);
            }}
            SelectDisplayProps={{ "data-testid": `history-${master.key}-speciality-filter` }}
          >
            <MenuItem value={SPECIALITY_FILTER_ANY}>Any Speciality</MenuItem>
            <MenuItem value={SPECIALITY_FILTER_ALL}>{ALL_SPECIALITIES_LABEL}</MenuItem>
            {specialityOptions.map((option) => (
              <MenuItem key={option._id} value={option._id}>
                {option.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {master.categories?.length > 0 && (
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel id={`history-${master.key}-category-label`} shrink>
              Category
            </InputLabel>
            <Select
              labelId={`history-${master.key}-category-label`}
              label="Category"
              notched
              displayEmpty
              value={categoryFilter}
              onChange={(event) => {
                setCategoryFilter(event.target.value);
                setPageNumber(0);
              }}
              SelectDisplayProps={{ "data-testid": `history-${master.key}-category-filter` }}
            >
              <MenuItem value="">All Categories</MenuItem>
              {master.categories.map((category) => (
                <MenuItem key={category} value={category}>
                  {category}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <FormControl size="small" sx={{ minWidth: 150, mr: "auto" }}>
          <InputLabel id={`history-${master.key}-status-label`} shrink>
            Status
          </InputLabel>
          <Select
            labelId={`history-${master.key}-status-label`}
            label="Status"
            notched
            displayEmpty
            value={statusFilter}
            onChange={(event) => {
              setStatusFilter(event.target.value);
              setPageNumber(0);
            }}
            SelectDisplayProps={{ "data-testid": `history-${master.key}-status-filter` }}
          >
            {STATUS_FILTER_OPTIONS.map((option) => (
              <MenuItem key={option.value || "any"} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button
          sx={btnStyle}
          variant="contained"
          disabled={busy}
          onClick={() => handleDownload("template", master.templateFile)}
        >
          Download Template
        </Button>

        <Button
          sx={btnStyle}
          variant="contained"
          disabled={busy}
          onClick={() => fileInputRef.current?.click()}
        >
          Import
        </Button>
        <input
          type="file"
          accept=".xlsx,.xls,.csv"
          ref={fileInputRef}
          hidden
          data-testid={`history-${master.key}-import-input`}
          onChange={handleImport}
        />

        <Button
          sx={btnStyle}
          variant="contained"
          disabled={busy}
          onClick={() => handleDownload("export", master.exportFile)}
        >
          Export
        </Button>

        <Button
          sx={{ ...btnStyle, bgcolor: "#194AF5", color: "white", ":hover": {} }}
          variant="contained"
          disabled={busy}
          onClick={() => {
            setActiveRecord({});
            setModalOpen(true);
          }}
        >
          Add
        </Button>
      </Box>

      {!loading && rows.length === 0 ? (
        <Box sx={{ py: 5, textAlign: "center", color: "#7D8DC4" }}>
          {master.emptyText}
        </Box>
      ) : (
        <Listtable
          tableheader={columns.headers}
          tablebody={rows}
          rowsPerPage={rowsPerPage}
          page={pageNumber}
          handleChangePage={(event, newPage) => setPageNumber(newPage)}
          handleChangeRowsPerPage={(event) => {
            setRowsPerPage(Number(event.target.value));
            setPageNumber(0);
          }}
          totalCount={count}
          dataKeyName={columns.keys}
          tag={`history-${master.key}`}
        />
      )}

      {modalOpen && (
        <PatientHistoryModal
          open={modalOpen}
          onClose={handleModalClose}
          data={activeRecord}
          api={api}
          masterLabel={master.label}
          categories={master.categories}
          specialityOptions={specialityOptions}
        />
      )}

      <Dialog
        open={Boolean(importResult)}
        onClose={() => setImportResult(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Import complete</DialogTitle>
        <DialogContent dividers>
          <DialogContentText sx={{ mb: 2 }}>{importResult?.message}</DialogContentText>
          <Box sx={{ fontWeight: 600, mb: 1 }}>Failed rows:</Box>
          <Box
            component="ul"
            aria-label="Failed rows"
            sx={{ listStyle: "none", p: 0, m: 0, maxHeight: 320, overflowY: "auto" }}
          >
            {(importResult?.failures ?? []).map((item, index) => (
              <Box
                component="li"
                key={`${item.row}-${item.name}-${index}`}
                sx={{ py: 1, borderBottom: "1px solid #EEE", fontSize: "0.875rem" }}
              >
                <Box sx={{ fontWeight: 600 }}>
                  {item.row ? `Row ${item.row}` : "Row"}
                  {item.name ? ` — ${item.name}` : ""}
                </Box>
                <Box sx={{ color: "#d32f2f" }}>Reason: {item.reason}</Box>
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportResult(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deactivateTarget)} onClose={() => setDeactivateTarget(null)}>
        <DialogTitle>Deactivate record?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Deactivate "{deactivateTarget?.name}" in {master.label}? It stays in
            this list as Inactive and can be set Active again from Edit.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeactivateTarget(null)} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={handleDeactivate} color="error" disabled={busy}>
            Deactivate
          </Button>
        </DialogActions>
      </Dialog>

      <Backdrop open={loading || busy} sx={{ zIndex: 1200, color: "#fff" }}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </>
  );
};

export default PatientHistoryTab;
